// SEQ-180 legibility follow-up (15 September 2026): what leaves this page as a
// file, as a pure module so both exports and their proofs share one answer.
//
// TWO DEFECTS, BOTH FOUND IN A DOWNLOADED FILE.
//
// 1. THE FIGURES WERE RAW FLOATS. The screen said "45.6%", "$750,878" and
//    "2.6%"; the CSV said 45.61054215979711, 750878.267 and
//    2.5953154242736356. Seventeen digits of a percentage is not more precise,
//    it is the division that produced it showing through, and it makes a
//    printed sheet unreadable and a pivot table ugly.
//
//    They stay NUMBERS, rounded to the precision the screen shows. Writing
//    "45.6%" as text would be worse than the float: a column of text does not
//    sum, average or sort, and this file exists to be worked on in a
//    spreadsheet.
//
// 2. THE FILENAME WAS A UTC DATE. `new Date().toISOString().split("T")[0]` is
//    the date in London, and Brisbane runs ten hours ahead of it. Every export
//    taken before 10am local was stamped with yesterday, every day, for every
//    user of this app. Formatted from the local calendar fields instead, which
//    is what "today" means to the person clicking the button.

import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";

/** The decimals a unit is shown to on screen, and therefore the decimals it is exported to. */
const EXPORT_DECIMALS: Record<PnlUnit, number> = {
  currency: 2,
  currency_per_hour: 2,
  // One, not two: formatPnlMetric sends percentages through formatPercentage,
  // which is `toFixed(1)`. The file has to agree with the screen it came from.
  percent: 1,
  hours: 1,
  count: 0,
  ratio: 2,
};

/** What a column header says the figure is in, where the label does not say so itself. */
const UNIT_SUFFIX: Record<PnlUnit, string> = {
  currency: " ($)",
  currency_per_hour: " ($/hr)",
  percent: " (%)",
  hours: " (hrs)",
  count: "",
  ratio: "",
};

/**
 * One exported figure: a number at the precision the screen shows it, or an
 * empty cell.
 *
 * A null becomes "" rather than 0. A project with no overtime data and a
 * project with no overtime are different facts, and a zero in the file would
 * make them the same one and drag every average down with it.
 */
export function exportNumber(value: number | null | undefined, unit: PnlUnit): number | string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  const decimals = EXPORT_DECIMALS[unit] ?? 2;
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * A column header carrying its unit.
 *
 * Only added where the label does not already carry it: "Profit %" and
 * "Patching % cost" say what they are, and "Profit % (%)" would be noise. A
 * column reading "Revenue" in a file opened next month says nothing about
 * dollars, so that one gets it.
 */
export function exportHeader(label: string, unit: PnlUnit | "text"): string {
  if (unit === "text") return label;
  const suffix = UNIT_SUFFIX[unit] ?? "";
  if (suffix === "") return label;
  const marker = suffix.slice(2, -1);
  return label.includes(marker) ? label : `${label}${suffix}`;
}

/**
 * Today, as the person downloading the file would write it.
 *
 * Built from the local calendar fields rather than from an ISO string, because
 * an ISO string is UTC and this app's users are ten hours east of it.
 */
export function localDateStamp(now: Date): string {
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** The whole filename, so the two export paths cannot drift apart. */
export function exportFilename(base: string, extension: string, now: Date): string {
  return `${base}-${localDateStamp(now)}.${extension}`;
}

/**
 * What the toast says once a file has been written.
 *
 * Both exports fired silently. A browser download that lands in a folder the
 * user is not looking at, with no confirmation on screen, is indistinguishable
 * from a button that does nothing, and the second click produces a second file.
 */
export function exportConfirmation(filename: string, rowCount: number): string {
  const rows = rowCount === 1 ? "1 project" : `${rowCount} projects`;
  return `${rows} saved to ${filename}.`;
}
